// NodeEditor.js
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Select,
  Tooltip,
  IconButton,
} from "@mui/material";
import ListItemWidget from "./ListItemWidget";
import KeywordBuilder from "./KeywordBuilder";
import DeleteIcon from "@mui/icons-material/Delete";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  addChildNode,
  addOutcomeBranch,
  addOutcomeNode,
  updateNodeField,
  deleteNode,
  deleteOutcomeBranch,
  setNodeErrors,
} from "./reducers/rulesetSlice";

/**
 * NodeEditor props:
 * - node: current node to edit (may be a subtree when focusing)
 * - nodeConfigs: node type configs (object)
 * - isOutcomeNode: boolean
 * - prefix: array of numbering indices for this node (relative prefix)
 * - absolutePrefix: OPTIONAL array of numbering indices to force absolute numbering for this node
 *
 * Behavior:
 * - The node's numbering is absolutePrefix (if provided) else prefix (if non-empty) else [1].
 * - Children are numbered by appending their (index+1) to the node's numbering.
 */

const NodeEditor = ({
  node,
  selectedCategory = null,
  nodeConfigs = {},
  isOutcomeNode = false,
  prefix = [],
  absolutePrefix = null,
}) => {
  const dispatch = useDispatch();

  // safe selector for node errors
  const nodeErrors = useSelector(
    (state) => state.s1Ruleset.errors[node?.id] || {},
  );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const has_advanced = useSelector(
    (state) => state.authen.loggedInUser?.advanced_licenses?.pathology,
  );

  // safe defaults for config to ensure validate exists and other arrays exist
  const config = useMemo(
    () =>
      nodeConfigs?.[node?.node] ?? {
        fields_config: [],
        validate: () => ({}),
        child_nodes: false,
        outcomes: [],
        children: [],
      },
    [node?.node, nodeConfigs],
  );

  // const availableTypes = useMemo(
  //   () => config.children ?? [],
  //   [config.children],
  // );

  //const allTypes = useMemo(() => Object.keys(nodeConfigs || {}), [nodeConfigs]);
  //const safeFirstType = availableTypes.length > 0 ? availableTypes[0] : "";

  const [newChildType, setNewChildType] = useState("");
  const [newOutcomeKey, setNewOutcomeKey] = useState("");
  const [newOutcomeType, setNewOutcomeType] = useState("");

  // Validate fields on change — config.validate is always callable (default provided above)
  useEffect(() => {
    try {
      const errors = (config.validate || (() => ({})))(node?.fields || {});
      dispatch(setNodeErrors({ nodeId: node.id, errors }));
    } catch (err) {
      // In case a validate throws, set a generic error so UI shows something
      dispatch(
        setNodeErrors({ nodeId: node.id, errors: { __validate: String(err) } }),
      );
    }
  }, [node?.fields, config, dispatch, node?.id]);

  // determine the absolute prefix for this node:
  // priority: absolutePrefix (explicit) -> prefix (if non-empty) -> default [1]
  const absolutePrefixForThisNode = Array.isArray(absolutePrefix)
    ? absolutePrefix
    : prefix && prefix.length > 0
      ? prefix
      : [1];

  const numberingStr = absolutePrefixForThisNode.join(".");

  const renderField = (field) => {
    const value =
      node?.fields?.[field.var] ?? (field.widget === "CheckBox" ? false : "");
    const width = field.width || 120;

    const commonSx = {
      minWidth: width,
      "& .MuiInputBase-input": { fontSize: "0.85rem" },
      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
      "& .MuiFormHelperText-root": { fontSize: "0.85rem" },
    };

    if (field.widget === "Combobox") {
      return (
        <FormControl
          size="small"
          error={!!nodeErrors[field.var]}
          sx={{ ...commonSx, minHeight: 69 }}
        >
          <Select
            size="small"
            value={value}
            displayEmpty
            onChange={(e) =>
              dispatch(
                updateNodeField({
                  nodeId: node.id,
                  field: field.var,
                  value: e.target.value,
                }),
              )
            }
            sx={commonSx}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {field.values?.map((v) => (
              <MenuItem key={v} value={v} sx={{ fontSize: "0.85rem", py: 0.5 }}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.widget === "ListItem") {
      return (
        <ListItemWidget
          node={node}
          field={field}
          value={value}
          error={nodeErrors[field.var]}
        />
      );
    }

    if (field.widget === "IconBox") {
      return (
        <Tooltip title={field.label}>
          <IconButton
            size="small"
            color={value ? "success" : "default"} // green if true, gray if false
            onClick={() => {
              const newValue = !value; // toggle true/false

              if (field.messageon || field.messageoff) {
                const message = newValue ? field.messageon : field.messageoff;
                const result = window.confirm(message);

                if (!result) {
                  window.alert("The action has been cancelled.");
                  return;
                }
              }

              dispatch(
                updateNodeField({
                  nodeId: node.id,
                  field: field.var,
                  value: newValue,
                }),
              );
            }}
          >
            {value ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
          </IconButton>
        </Tooltip>
      );
    }

    if (field.widget === "CheckBox") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => {
                const newValue = e.target.checked;

                // Check if messages are configured for this field
                if (field.messageon || field.messageoff) {
                  const message = newValue ? field.messageon : field.messageoff;
                  const result = window.confirm(message);

                  if (result) {
                    dispatch(
                      updateNodeField({
                        nodeId: node.id,
                        field: field.var,
                        value: newValue,
                      }),
                    );
                  } else {
                    // Cancel the checkbox toggle visually
                    e.preventDefault();
                    window.alert("The action has been cancelled.");
                  }
                } else {
                  // Default behavior (no confirmation required)
                  dispatch(
                    updateNodeField({
                      nodeId: node.id,
                      field: field.var,
                      value: newValue,
                    }),
                  );
                }
              }}
              size="small"
            />
          }
          label={field.label}
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
        />
      );
    }
    if (field.widget === "RadioButton") {
      return (
        <FormControl
          component="fieldset"
          error={!!nodeErrors[field.var]}
          sx={{ ...commonSx, minHeight: 69 }}
        >
          <FormLabel component="legend" sx={{ fontSize: "0.85rem", mb: 0.5 }}>
            {field.label}
          </FormLabel>
          <RadioGroup
            row
            value={value}
            onChange={(e) =>
              dispatch(
                updateNodeField({
                  nodeId: node.id,
                  field: field.var,
                  value: e.target.value,
                }),
              )
            }
          >
            {field.values?.map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio size="small" />}
                label={v}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            ))}
          </RadioGroup>
          {nodeErrors[field.var] && (
            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
              {nodeErrors[field.var]}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.widget === "KeywordBuilder") {
      return (
        <KeywordBuilder
          value={value}
          onChange={(serialized) =>
            dispatch(
              updateNodeField({
                nodeId: node.id,
                field: field.var,
                value: serialized,
              }),
            )
          }
          error={nodeErrors[field.var]}
          disabled={false}
        />
      );
    }

    return (
      <Tooltip title={field.tooltip || ""} arrow>
        <TextField
          label={field.label}
          value={value}
          type={
            field.type === "float" || field.type === "int" ? "number" : "text"
          }
          size="small"
          onChange={(e) =>
            dispatch(
              updateNodeField({
                nodeId: node.id,
                field: field.var,
                value: e.target.value,
              }),
            )
          }
          sx={{ width: field.width ?? 200, ...commonSx }}
          error={!!nodeErrors[field.var]}
          helperText={nodeErrors[field.var] || " "}
          disabled={field.editable === false}
          InputProps={{
            readOnly: field.editable === "readonly",
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 1.5,
        background: isOutcomeNode ? "#f9f9ff" : "#fff",
        border: isOutcomeNode ? "1px dashed #6c63ff" : "1px solid #e0e0e0",
        pointerEvents: has_write && has_advanced ? "auto" : "none",
      }}
      elevation={isOutcomeNode ? 1 : 2}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Numbering & Node name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {numberingStr && (
            <Button
              size="small"
              variant="outlined"
              sx={{ minWidth: 36, width: 36, height: 28, fontSize: "0.8rem" }}
            >
              {numberingStr}
            </Button>
          )}
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: isOutcomeNode ? 600 : 400,
              color: isOutcomeNode ? "#3f3d9f" : "inherit",
            }}
          >
            {node?.node === "Category Check"
              ? `Category - ${selectedCategory}`
              : node?.node}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        {/* Add child node (only if allowed) */}
        {config.child_nodes && (
          <Box
            sx={{
              background: "#faf5ff",
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            <Select
              size="small"
              value={newChildType}
              onChange={(e) => setNewChildType(e.target.value)}
              sx={{
                "& .MuiInputBase-input": {
                  padding: "2px 6px",
                  fontSize: "0.85rem",
                },
              }}
            >
              {config.children?.map((child) => (
                <MenuItem
                  key={child}
                  value={child}
                  sx={{ fontSize: "0.85rem", py: 0.5 }}
                >
                  {child}
                </MenuItem>
              )) || null}
            </Select>
            <Button
              size="small"
              sx={{ fontSize: "0.75rem", px: 1, py: 0.2 }}
              onClick={() => {
                if (newChildType) {
                  dispatch(
                    addChildNode({ parentId: node.id, nodeType: newChildType }),
                  );
                }
              }}
            >
              ➕ Add Rule
            </Button>
          </Box>
        )}

        {/* Add outcome branch (only if outcomes exist) */}
        {config.outcomes && (
          <Box
            sx={{
              background: "#f7faf7",
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            {node?.node === "Launch Protocol" ? (
              <TextField
                size="small"
                placeholder="Enter outcome key"
                value={newOutcomeKey}
                onChange={(e) => setNewOutcomeKey(e.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "4px 8px",
                    fontSize: "0.85rem",
                  },
                }}
              />
            ) : (
              <Select
                size="small"
                value={newOutcomeKey}
                onChange={(e) => setNewOutcomeKey(e.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "2px 6px",
                    fontSize: "0.85rem",
                  },
                }}
              >
                {config.outcomes.map((outcome) => (
                  <MenuItem
                    key={outcome}
                    value={outcome}
                    sx={{ fontSize: "0.85rem", py: 0.5 }}
                  >
                    {outcome}
                  </MenuItem>
                ))}
              </Select>
            )}
            <Button
              size="small"
              sx={{ fontSize: "0.75rem", px: 1, py: 0.2 }}
              onClick={() => {
                if (newOutcomeKey) {
                  dispatch(
                    addOutcomeBranch({
                      nodeId: node.id,
                      outcomeKey: newOutcomeKey,
                    }),
                  );
                }
              }}
            >
              ➕ Add Outcome
            </Button>
          </Box>
        )}
        {config.has_deactivated && (
          <Tooltip title="">
            <Button
              size="small"
              variant="contained"
              color={node?.fields?.deactivated ? "inherit" : "success"}
              onClick={() => {
                const newValue = !node?.fields?.deactivated;

                const message = newValue ? "Deactivate?" : "Activate?";
                const confirmed = window.confirm(message);

                if (!confirmed) return;

                dispatch(
                  updateNodeField({
                    nodeId: node.id,
                    field: "deactivated",
                    value: newValue,
                  }),
                );
              }}
            >
              {node?.fields?.deactivated ? "Deactive" : "Active"}
            </Button>
          </Tooltip>
        )}
        {config.has_terminate && (
          <Tooltip title="">
            <Button
              size="small"
              variant="contained"
              color={node?.fields?.terminate ? "inherit" : "success"}
              onClick={() => {
                const newValue = !node?.fields?.terminate;

                const message = newValue ? "Terminate?" : "Continue?";
                const confirmed = window.confirm(message);

                if (!confirmed) return;

                dispatch(
                  updateNodeField({
                    nodeId: node.id,
                    field: "terminate",
                    value: newValue,
                  }),
                );
              }}
            >
              {node?.fields?.terminate ? "Terminate" : "Continue"}
            </Button>
          </Tooltip>
        )}

        {config.has_inreview && (
          <Tooltip title="">
            <Button
              size="small"
              variant="contained"
              color={
                (node?.fields?.inreview ?? "Enabled") === "Enabled"
                  ? "success"
                  : "inherit"
              }
              onClick={() => {
                const current = node?.fields?.inreview ?? "Enabled";
                let newValue;

                if (current === "In-Review") {
                  newValue = "Enabled";
                } else {
                  newValue = current === "Enabled" ? "Disabled" : "Enabled";
                }
                const message = newValue === "Enabled" ? "Enable?" : "Disable?";

                const confirmed = window.confirm(message);
                if (!confirmed) return;

                dispatch(
                  updateNodeField({
                    nodeId: node.id,
                    field: "inreview",
                    value: newValue,
                  }),
                );
              }}
            >
              {(node?.fields?.inreview ?? "Enabled") === "Enabled"
                ? "Enabled"
                : (node?.fields?.inreview ?? "Enabled") === "Disabled"
                  ? "Disabled"
                  : "In-Review"}
            </Button>
          </Tooltip>
        )}

        {config.has_needsreview && (
          <Tooltip title="">
            <Button
              size="small"
              variant="contained"
              color={node?.fields?.needs_review ? "inherit" : "success"}
              onClick={() => {
                const newValue = !node?.fields?.needs_review;

                const message = newValue
                  ? "Needs Review and Approval?"
                  : "File Directly?";
                const confirmed = window.confirm(message);

                if (!confirmed) return;

                dispatch(
                  updateNodeField({
                    nodeId: node.id,
                    field: "needs_review",
                    value: newValue,
                  }),
                );
              }}
            >
              {node?.fields?.needs_review
                ? "Review and Approval"
                : "File Directly"}
            </Button>
          </Tooltip>
        )}

        <IconButton
          size="small"
          color="error"
          onClick={() => {
            const result = window.confirm(
              "Are you sure you want to delete this node?",
            );
            if (result) {
              dispatch(deleteNode({ nodeId: node.id }));
            } else {
              window.alert("The action has been cancelled.");
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Fields */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          mt: 1.5,
          alignItems: "center",
        }}
      >
        {config.fields_config?.map((field) => {
          if (field.var === "inreview") return null; // skip this field
          if (field.var === "deactivated") return null; // skip this field
          if (field.var === "terminate") return null; // skip this field
          if (field.var === "needs_review") return null; // skip this field

          return (
            <Box
              key={field.var}
              sx={{
                flexBasis:
                  field.widget === "RadioButton" || field.widget === "ListItem"
                    ? "100%"
                    : "auto",
              }}
            >
              {renderField(field)}
            </Box>
          );
        }) || null}
      </Box>

      {/* Child nodes (propagate numbering based on array index) */}
      {node?.nodes?.length > 0 && (
        <Box sx={{ ml: 2, mt: 1 }}>
          {node.nodes.map((child, idx) => (
            <NodeEditor
              key={child.id}
              node={child}
              nodeConfigs={nodeConfigs}
              isOutcomeNode={false}
              // child numbering = parent absolute prefix + child index
              prefix={[...absolutePrefixForThisNode, idx + 1]}
            />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Outcome nodes */}
      {node.outcomes &&
        Object.entries(node.outcomes).map(([outcomeKey, nodes]) => (
          <Box key={outcomeKey} sx={{ ml: 1.5, mt: 0.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "0.85rem", color: "#6c63ff" }}
              >
                Outcome: {outcomeKey}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const result = window.confirm(
                    "Are you sure you want to delete this outcome?",
                  );
                  if (result) {
                    dispatch(
                      deleteOutcomeBranch({ nodeId: node.id, outcomeKey }),
                    );
                  } else {
                    window.alert("The action has been cancelled.");
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {nodes.map((outNode, idx) => (
              <NodeEditor
                key={outNode.id}
                node={outNode}
                nodeConfigs={nodeConfigs}
                isOutcomeNode={true}
                prefix={[...absolutePrefixForThisNode, idx + 1]}
              />
            ))}

            {/* Add node to outcome */}
            <Box
              sx={{
                width: 200,
                background: "#fff7e6",
                mt: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Select
                size="small"
                value={newOutcomeType}
                onChange={(e) => setNewOutcomeType(e.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "2px 6px",
                    fontSize: "0.85rem",
                  },
                }}
              >
                {config.outcome_children?.map((child) => (
                  <MenuItem
                    key={child}
                    value={child}
                    sx={{ fontSize: "0.85rem", py: 0.5 }}
                  >
                    {child}
                  </MenuItem>
                )) || null}
              </Select>
              <Button
                size="small"
                sx={{ fontSize: "0.75rem", px: 1, py: 0.2 }}
                onClick={() => {
                  if (newOutcomeType) {
                    dispatch(
                      addOutcomeNode({
                        parentId: node.id,
                        outcomeKey,
                        nodeType: newOutcomeType,
                      }),
                    );
                  }
                }}
              >
                ➕ Add Rule to {outcomeKey}
              </Button>
            </Box>
          </Box>
        ))}
    </Paper>
  );
};

export default NodeEditor;
